"use client";

import { OnboardingProvider } from "@/components/onboarding/onboarding-provider";
import { Toaster } from "@/components/ui/sonner";
import { DEFAULT_THEME, DEFAULT_TOAST_DURATION, isDevMode } from "@/constants";
import { usePreviousRoutes } from "@/hooks/use-previous-route";
import { queryClient } from "@/lib/query-client";
import { AuthDialogProvider } from "@/providers/auth-dialog-provider";
import { PushProvider } from "@/providers/push-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MotionConfig, Transition } from "motion/react";
import { ThemeProvider } from "next-themes";
import React from "react";
import AuthSessionProvider from "./auth-session-provider";

const defaultTransition = {
  type: "spring",
  duration: 0.3,
  bounce: 0,
} satisfies Transition;

interface Props {
  readonly children: React.ReactNode;
}

export function Providers({ children }: Props) {
  return (
    <>
      <AuthSessionProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            attribute="class"
            defaultTheme={DEFAULT_THEME}
            enableSystem={false}
          >
            <OnboardingProvider>
              <MotionConfig transition={defaultTransition}>
                <AuthDialogProvider>{children}</AuthDialogProvider>
              </MotionConfig>
            </OnboardingProvider>
          </ThemeProvider>
          <Toaster duration={DEFAULT_TOAST_DURATION} position="top-right" />
          <PushProvider />
          {isDevMode ? (
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="bottom-right"
            />
          ) : null}
        </QueryClientProvider>
      </AuthSessionProvider>
      <RouteHistory />
    </>
  );
}

function RouteHistory() {
  usePreviousRoutes();
  return null;
}
